import type { Language } from "@/lib/i18n";

// TECH-DEBT: monolith — this file has grown to 900+ lines. Module-specific types should
// be co-located with their API module (see TECH-DEBT split candidate comments below).
// Note: ReferralStatus in referrals.ts is the intended pattern — local, co-located types.
// Naming inconsistency: some module-specific types live here, others (ReferralStatus) are
// co-located. Prefer co-location going forward when adding new types.
export type Fiat =
  | "USD"
  | "EUR"
  | "GBP"
  | "SEK"
  | "CHF"
  | "HKD"
  | "CAD"
  | "JPY"
  | "DKK"
  | "CNY"
  | "AUD"
  | "PLN"
  | "CZK"
  | "NOK"
  | "TWD"
  | "KZT"
  | "KRW";

// The only hardcoded currency-code list left in the frontend — needed because
// `Fiat` is a compile-time type (can't be derived from a runtime fetch) and a
// couple of low-level utilities (normalizeFiat, transaction validation) must
// run synchronously outside React, before any query can resolve. Every other
// currency picker/filter fetches GET /currencies instead (see useCurrencies).
// Keep this in sync with `Fiat` and with core/constants.USER_SELECTABLE_CURRENCIES.
export const ALL_FIAT_CODES: Fiat[] = [
  "EUR",
  "USD",
  "GBP",
  "SEK",
  "CHF",
  "HKD",
  "CAD",
  "JPY",
  "DKK",
  "CNY",
  "AUD",
  "PLN",
  "CZK",
  "NOK",
  "TWD",
  "KZT",
  "KRW",
];

export interface CurrencyOption {
  code: Fiat;
  label: string;
}

export type ListingStatus = "ACTIVE" | "DELISTED";

export interface PortfolioPayload {
  name: string;
  brokerage: string;
  base_currency: Fiat;
  track_cash?: boolean;
  api_key?: string;
  custom_asset_class?: CustomAssetClass; // required when brokerage === 'custom'
  logo_key?: string; // only used for manual and custom portfolios
}

export interface Portfolio {
  id: number;
  name: string;
  brokerage?: string | null;
  base_currency: Fiat;
  total_value: number;
  invested_capital: number;
  unrealized_gain_base_total: number;
  realized_sales_gain_base_total: number;
  dividend_base_total: number;
  interest_base_total: number;
  realized_gain_total: number;
  fx_pnl_base_total: number;
  /** Part of invested_capital that came from broker rewards, not the user's own money. */
  reward_base_total?: number;
  total_gain_loss: number;
  total_gain_loss_percent: number;
  holdings_value: number;
  holdings_gain_loss: number;
  holdings_gain_loss_percent: number;
  cash_total: number;
  cash_balances?: Record<string, number>;
  irr_inception?: number | null;
  twr_inception?: number | null;
  performance_refresh_pending?: boolean;
  /** Open positions. */
  holdings_count?: number;
  /** Biggest position by value: its ticker, and its share of holdings value (0 to 100). */
  largest_position_ticker?: string | null;
  largest_position_pct?: number | null;
  is_combined?: boolean;
  track_cash?: boolean;
  include_in_rollup?: boolean;
  has_api_key?: boolean;
  custom_asset_class?: string | null;
  logo_key?: string | null;
  /** Metric keys chosen for the Portfolios page card. Null shows the default six. */
  card_metrics?: string[] | null;
  display_order?: number;
  last_synced_at?: string | null;
  /** Free tier: viewable but read only, and left out of the "All portfolios" total. */
  is_locked?: boolean;
  /** The demo portfolio. Read only for everyone and never counts toward the limit. */
  is_demo?: boolean;
}

export interface Listing {
  id: number;
  provider: string;
  provider_symbol: string | null;
  ticker: string;
  exchange: string;
  currency: string;
  isin?: string | null;
  company_name?: string | null;
  status: ListingStatus;
  delisted_at?: string | null;
  has_financial_reports?: boolean;
  canonical_ticker?: string | null;
  asset_class?: string | null;
  sector?: string | null;
}

export interface ListingSearchResult {
  id: number;
  ticker: string;
  exchange: string;
  currency: string;
  company_name: string;
  display_name: string;
  has_financial_reports?: boolean;
  dividend_country_warning?: string | null;
}

export interface ExternalListingProviderDetails {
  source: string;
  symbol: string;
  exchange?: string | null;
  region?: string | null;
  raw_exchange?: string | null;
  quote_type?: string | null;
  score?: number | null;
}

export interface ExternalMarketSearchResult {
  ticker: string;
  name?: string | null;
  exchange: string;
  currency?: string | null;
  display_symbol?: string | null;
  display_name?: string | null;
  region?: string | null;
  isin?: string | null;
  provider_details: ExternalListingProviderDetails;
}

export interface NewListingDetails {
  ticker: string;
  exchange: string;
  currency: string;
  company_name?: string | null;
  isin?: string | null;
  provider_details: ExternalListingProviderDetails;
}

export interface BackendHolding {
  listing: Listing;
  ticker: string;
  quantity: number;
  native_currency: string;
  total_cost_native: number;
  total_cost_base: number;
  average_cost_per_share_native: number;
  average_cost_per_share_base: number;
  company_name: string;
  current_price: number;
  current_price_currency: Fiat | null;
  current_price_base?: number | null;
  market_value_native?: number | null;
  market_value_base?: number | null;
  /**
   * The instrument's own quote, set only when it is quoted in a different currency than the
   * position was bought in — Robinhood Europe pays EUR for a USD-quoted instrument, so
   * current_price alone would state a price the instrument does not have. Null on every
   * other brokerage, where current_price is already the real quote.
   */
  quote_currency?: Fiat | null;
  current_price_quote?: number | null;
  market_value_quote?: number | null;
  price_source?: string | null;
  price_as_of?: string | null;
  day_change_native?: number | null;
  day_change_base?: number | null;
  day_change_percent?: number | null;
}

export interface HistoricalValuePoint {
  date: string;
  value: number;
  invested?: number;
}

export interface HistoricalValueResponse {
  data?: HistoricalValuePoint[];
  combined?: HistoricalValuePoint[];
  portfolios?: Record<string, HistoricalValuePoint[]>;
}

export type NumericLike = string | number | null | undefined;

export interface RawListingResponse {
  id: number;
  provider: string;
  provider_symbol?: string | null;
  ticker: string;
  exchange: string;
  currency: string;
  isin?: string | null;
  company_name?: string | null;
  status?: ListingStatus;
  delisted_at?: string | null;
}

export interface RawTransactionResponse {
  id: number;
  portfolio_id: number;
  date: string;
  type: TransactionType;
  listing_id?: number | null;
  listing?: RawListingResponse | null;
  ticker?: string | null;
  quantity?: NumericLike;
  price_native?: NumericLike;
  price_in_listing_currency?: NumericLike;
  fee_native?: NumericLike;
  fee_currency?: string | null;
  currency_native?: string | null;
  base_to_native_fx_rate?: NumericLike;
  from_currency?: string | null;
  from_amount?: NumericLike;
  to_currency?: string | null;
  to_amount?: NumericLike;
  withholding_tax_native?: NumericLike;
  isin?: string | null;
  settlement_amount?: NumericLike;
  settlement_currency?: string | null;
  notes?: string | null;
  total_value?: NumericLike;
}

export interface RawPortfolioResponse {
  id: number;
  name: string;
  brokerage?: string | null;
  base_currency: Fiat;
  total_value: NumericLike;
  invested_capital: NumericLike;
  unrealized_gain_base_total: NumericLike;
  realized_sales_gain_base_total: NumericLike;
  dividend_base_total: NumericLike;
  interest_base_total: NumericLike;
  realized_gain_total: NumericLike;
  fx_pnl_base_total?: NumericLike;
  /** Part of invested_capital that came from broker rewards, not the user's own money. */
  reward_base_total?: NumericLike;
  total_gain_loss: NumericLike;
  total_gain_loss_percent: NumericLike;
  holdings_value: NumericLike;
  holdings_gain_loss: NumericLike;
  holdings_gain_loss_percent: NumericLike;
  cash_total: NumericLike;
  irr_inception?: NumericLike;
  twr_inception?: NumericLike;
  performance_refresh_pending?: unknown;
  holdings_count?: number;
  largest_position_ticker?: string | null;
  largest_position_pct?: NumericLike;
  cash_balances?: Portfolio["cash_balances"];
  is_combined?: boolean;
  track_cash?: boolean;
  include_in_rollup?: boolean;
  has_api_key?: boolean;
  custom_asset_class?: string | null;
  logo_key?: string | null;
  card_metrics?: string[] | null;
  display_order?: number;
  last_synced_at?: string | null;
  /** Free tier: viewable but read only, and left out of the "All portfolios" total. */
  is_locked?: boolean;
  /** The demo portfolio. Read only for everyone and never counts toward the limit. */
  is_demo?: boolean;
}

export interface RawRealizedGainResponse {
  id: number;
  portfolio_id: number;
  listing_id: number;
  listing?: RawListingResponse | null;
  sell_date: string;
  quantity_sold?: NumericLike;
  native_currency: string;
  cost_basis_of_sold_shares_native?: NumericLike;
  cost_basis_of_sold_shares_base?: NumericLike;
  proceeds_from_sale_native?: NumericLike;
  proceeds_from_sale_base?: NumericLike;
  realized_gain_loss_native?: NumericLike;
  realized_gain_loss_base?: NumericLike;
  fx_impact_base_currency?: NumericLike;
}

export enum TransactionType {
  BUY = "BUY",
  SELL = "SELL",
  CONVERSION = "CONVERSION",
  DEPOSIT = "DEPOSIT",
  WITHDRAWAL = "WITHDRAWAL",
  DIVIDEND = "DIVIDEND",
  INTEREST = "INTEREST",
  FEE = "FEE",
  TRANSFER_IN = "TRANSFER_IN",
  TRANSFER_OUT = "TRANSFER_OUT",
  REWARD = "REWARD",
}

export interface ManualTransactionCreate {
  date: string;
  type: TransactionType;
  listing_id?: number;
  new_listing_details?: NewListingDetails;
  price_native?: number;
  fee_native?: number;
  fee_currency?: Fiat;
  quantity?: number;
  currency_native?: Fiat;
  from_currency?: Fiat;
  from_amount?: number;
  to_currency?: Fiat;
  to_amount?: number;
  withholding_tax_native?: number;
  isin?: string;
  exchange_rate?: number;
  notes?: string;
}

// TECH-DEBT: split candidate — CustomAssetClass, SOURCED_ASSET_CLASSES, CA_SERIES,
// CaResolveRequest, isSourcedAssetClass, CustomTxRow, CustomHoldingCreate,
// CustomTransactionsCreate, CustomHolding, CustomPriceCreate, CustomScreenshotRow,
// CustomScreenshotImportResponse, CustomPrice could be co-located with customAssets.ts
// --- Custom (user-owned, manually-priced) assets ---
// A custom portfolio is a typed container; holdings inherit its `custom_asset_class`.
export type CustomAssetClass =
  | "PENSION_FUND"
  | "BOND"
  | "TERM_DEPOSIT"
  | "CUSTOM"
  | "CERTIFICADO_AFORRO"
  | "CERTIFICADO_TESOURO"
  | "PPR";

// SOURCED classes are global, externally-priced assets: the user picks an existing
// listing instead of typing a name, there's no manual value-history, and prices come from
// the daily cron. PPR is FT-scraped NAV; CERTIFICADO_AFORRO is computed from the IGCP rate
// table (no free-text search — the listing is resolved from series + subscription date via
// POST /custom-assets/ca/resolve). Mirrors the backend SOURCED_ASSET_CLASSES.
export const SOURCED_ASSET_CLASSES: CustomAssetClass[] = [
  "PPR",
  "CERTIFICADO_AFORRO",
];

// CA-specific: the two series currently supported (E closed to new subscriptions in 2023
// but still held by existing users; F is open). See pythonbackend/services/ca_rate_generator.py.
export const CA_SERIES: { value: "E" | "F"; label: string }[] = [
  { value: "F", label: "Série F" },
  { value: "E", label: "Série E" },
];

export interface CaResolveRequest {
  series: "E" | "F";
  subscription_date: string; // YYYY-MM-DD
}

export const isSourcedAssetClass = (
  ac?: CustomAssetClass | string | null,
): boolean => ac != null && (SOURCED_ASSET_CLASSES as string[]).includes(ac);

// A single resolved contribution/withdrawal for a custom holding. The UI lets the user
// enter any 2 of {units, price/unit, total}; the third is derived client-side so the API
// always receives the resolved quantity + the actual price paid.
export interface CustomTxRow {
  date: string; // YYYY-MM-DD
  quantity: number;
  price: number; // actual price per unit paid
  type: "BUY" | "SELL";
}

export interface CustomHoldingCreate {
  portfolio_id: number;
  name?: string; // MANUAL path: the holding name
  listing_id?: number; // SOURCED path: an existing global listing (e.g. a PPR)
  transactions: CustomTxRow[];
}

export interface CustomTransactionsCreate {
  portfolio_id: number;
  transactions: CustomTxRow[];
}

export interface CustomHolding {
  listing_id: number;
  name: string | null;
  ticker: string;
  currency: string;
  asset_class: string;
}

export interface CustomPriceCreate {
  date: string; // YYYY-MM-DD
  price: number;
}

// A transaction row extracted from a PPR/fund portal screenshot by the vision LLM.
// Any field may be null; the UI maps the available 2 of {units, total, price} onto a row.
export interface CustomScreenshotRow {
  date: string | null;
  units: number | null;
  total: number | null;
  price: number | null;
}

export interface CustomScreenshotImportResponse {
  rows: CustomScreenshotRow[];
}

export interface CustomPrice {
  id: number;
  date: string;
  price: number;
}

// TECH-DEBT: possibly dead — ManualTransaction is only referenced by the removed
// addTransaction wrapper (confirmed unused by grep). Safe to delete if addTransaction is gone.
export type ManualTransaction = ManualTransactionCreate & {
  portfolio_id: number;
};

export interface Transaction {
  id: number;
  portfolio_id: number;
  date: string;
  type: TransactionType;
  listing_id?: number | null;
  listing?: Listing | null;
  ticker?: string | null;
  quantity?: number | null;
  price_native?: number | null;
  /**
   * price_native converted into the listing's own quote currency. Only present on the
   * paginated endpoint, and only when the two differ — a broker that reports in the account
   * currency (Robinhood Europe) pays for a USD-quoted instrument in EUR. Null/absent means
   * price_native is already in the listing's currency.
   */
  price_in_listing_currency?: number | null;
  fee_native?: number | null;
  fee_currency?: Fiat | null;
  currency_native?: Fiat | null;
  base_to_native_fx_rate?: number | null;
  from_currency?: Fiat | null;
  from_amount?: number | null;
  to_currency?: Fiat | null;
  to_amount?: number | null;
  withholding_tax_native?: number | null;
  isin?: string | null;
  settlement_amount?: number | null;
  settlement_currency?: Fiat | null;
  notes?: string | null;
  total_value?: number | null;
}

// TECH-DEBT: split candidate — DividendSummaryResponse, DividendChartsResponse,
// DividendChartPoint, DividendIncomeByHolding could be co-located with dividends.ts
export interface DividendSummaryResponse {
  ytd_total: number;
  all_time_total: number;
  projected_annual: number;
  yield_on_cost: number | null;
  yoc_denominator_zero: boolean;
  current_yield: number | null;
  current_yield_denominator_zero: boolean;
}

export interface DividendChartPoint {
  label: string;
  value: number | null;
}

export interface DividendIncomeByHolding {
  ticker: string;
  amount: number;
}

export interface DividendChartsResponse {
  monthly_chart: DividendChartPoint[];
  annual_chart: DividendChartPoint[];
  income_by_holding: DividendIncomeByHolding[];
}

// The backend serializes Decimal fields as JSON strings (Pydantic v2 default), not
// numbers — same reason RawTransactionResponse's numeric fields are typed NumericLike.
// Consumers must Number()-coerce these before doing arithmetic or calling .toFixed.
export interface DividendByHoldingItem {
  listing_id: number;
  ticker: string;
  exchange: string;
  company_name: string | null;
  quantity: NumericLike;
  is_sold: boolean;
  cost_basis_base: NumericLike;
  market_value_base: NumericLike;
  dividend_yield: NumericLike;
  yield_on_cost: NumericLike;
  dividends_received_base: NumericLike;
  dividends_received_native: NumericLike;
  deposited_dividends_base: NumericLike;
  deposited_dividends_native: NumericLike;
  total_recovered_pct: NumericLike;
  payment_count: number;
  last_payment_date: string | null;
}

export interface DividendByHoldingResponse {
  items: DividendByHoldingItem[];
}

export interface AnalyticsYearPointResponse {
  year: number;
  deposits: number;
  withdrawals: number;
  fees: number;
  interest: number;
  dividends: number;
  realized_gains: number;
  avg_portfolio_value: number;
  avg_cash_held: number;
  months_with_deposit: number;
  months_elapsed: number;
}

export interface AllocationBreakdownResponse {
  invested: number;
  cash: number;
  withdrawn: number;
}

export interface AnalyticsCashFlowResponse {
  base_currency: string;
  years: AnalyticsYearPointResponse[];
  allocation: AllocationBreakdownResponse;
}

// Decimal fields serialize as JSON strings (Pydantic v2 default) — see the note on
// DividendByHoldingItem above. Number()-coerce before doing arithmetic.
export interface GoalsHistoryPointResponse {
  // ISO date (YYYY-MM-DD) of the snapshot this value came from — the current year's
  // point is today's date, not January 1st.
  date: string;
  value: NumericLike;
  // Running net invested capital (deposits minus withdrawals) on that date.
  invested: NumericLike;
}

export interface GoalsBaselineResponse {
  current_value: NumericLike;
  currency: string;
  avg_annual_return_pct: number;
  return_variance_pct: number;
  avg_monthly_deposit: NumericLike;
  total_deposited: NumericLike;
  history: GoalsHistoryPointResponse[];
}

export interface SavedGoalResponse {
  id: number;
  portfolio_id: number;
  name: string;
  target_year: number;
  solve_for: "value" | "deposit" | "return" | "year";
  annual_return_pct: NumericLike;
  monthly_deposit: NumericLike;
  target_value: NumericLike;
  inflation_pct: NumericLike;
  created_at: string;
  updated_at: string;
}

export interface SavedGoalInput {
  name: string;
  target_year: number;
  solve_for: "value" | "deposit" | "return" | "year";
  annual_return_pct: number;
  monthly_deposit: number;
  target_value: number;
  inflation_pct: number;
}

export interface SavedRetirementScenarioResponse {
  id: number;
  portfolio_id: number;
  name: string;
  duration_years: number;
  solve_for: "value" | "year" | "withdrawal";
  annual_return_pct: NumericLike;
  monthly_withdrawal: NumericLike;
  starting_value: NumericLike;
  inflation_pct: NumericLike;
  dividend_yield_pct: NumericLike;
  dividend_tax_pct: NumericLike;
  capital_gains_tax_pct: NumericLike;
  created_at: string;
  updated_at: string;
}

export interface SavedRetirementScenarioInput {
  name: string;
  duration_years: number;
  solve_for: "value" | "year" | "withdrawal";
  annual_return_pct: number;
  monthly_withdrawal: number;
  starting_value: number;
  inflation_pct: number;
  dividend_yield_pct: number;
  dividend_tax_pct: number;
  capital_gains_tax_pct: number;
}

// Always shown in native currency, gross — no FX conversion, no tax handling.
export interface DividendCalendarEntry {
  pay_date: string;
  ticker: string;
  company_name: string | null;
  exchange: string | null;
  status: "paid" | "upcoming";
  quantity: number;
  currency_native: Fiat | null;
  // Paid only: per-share amount (gross or net per the calendar's mode) from the actual
  // DIVIDEND transaction.
  price_native: number | null;
  // Upcoming only: estimated from the listing's most recent actual payment.
  estimated_amount_per_share: number | null;
  estimated_total: number | null;
}

export interface DividendCalendarMonthTotal {
  month: number; // 1-12
  received: number;
  to_be_received: number;
  total: number;
}

export interface DividendCalendarSummaryResponse {
  received: number;
  to_be_received: number;
  total: number;
  by_month: DividendCalendarMonthTotal[];
}

export interface DividendCalendarResponse {
  year: number;
  entries: DividendCalendarEntry[];
}

export interface HoldingsApiResponse {
  holdings: BackendHolding[];
  fx_rates: Record<string, number>;
  cash_balances?: Record<string, number>;
  price_as_of?: string | null;
}

export interface HistoricalExchangeRate {
  date: string;
  from_currency: string;
  to_currency: string;
  rate: number;
}

// TECH-DEBT: split candidate — ApiUser could be co-located with auth.ts
export interface ApiUser {
  id: number;
  username: string;
  is_email_verified: boolean;
  is_ff: boolean;
  created_at: string;
  portfolios?: Portfolio[];
}

// TECH-DEBT: split candidate — AttachmentPayload, ContactFormData, ContactFormPublicExtras,
// ContactFormPayload could be co-located with contact.ts
export interface AttachmentPayload {
  name: string;
  content: string; // raw base64, no data URL prefix
}

export interface ContactFormData {
  name: string;
  reply_to_email: string;
  subject: string;
  message_content: string;
  attachments?: AttachmentPayload[];
  hp_website?: string;
  elapsed_ms?: number;
  cf_turnstile_token?: string;
}

export interface ContactFormPublicExtras {
  hp_website?: string;
  elapsed_ms?: number;
  cf_turnstile_token?: string;
}

export type ContactFormPayload = ContactFormData & ContactFormPublicExtras;

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface TransactionQueryParams {
  page?: number;
  size?: number;
  sort?: string;
  dir?: "asc" | "desc";
  ticker?: string;
  date_from?: string;
  date_to?: string;
  types?: string[];
}

export interface PotentialMatch {
  symbol: string;
  name?: string;
  type?: string;
  region?: string;
  exchange?: string;
  raw_exchange?: string;
  market_open?: string;
  market_close?: string;
  timezone?: string;
  currency?: string;
  match_score?: number | null;
  score?: number | null;
  confidence?: string;
  breakdown?: Record<string, number>;
  quote_type?: string;
  base_symbol?: string;
  isin?: string;
  mic?: string;
  source?: string;
}

export interface UnresolvedTransaction {
  transaction_data: Record<string, unknown>;
  potential_matches: PotentialMatch[];
  row_number: number;
}

export interface ImportIssueTransaction {
  original_data: Record<string, unknown>;
  invalid_reason: string;
  row_number: number;
}

export interface UnsupportedTickerTransaction {
  ticker: string;
  currency: string;
  isin?: string | null;
  exchange?: string | null;
  company_name?: string | null;
  row_number: number;
}

export interface UploadTransactionsFileResult {
  filename: string;
  total_detected: number;
  success_count: number;
  error_count: number;
  errors: string[];
  user_error?: boolean;
  invalid_transactions: ImportIssueTransaction[];
  unsupported_currency_transactions: ImportIssueTransaction[];
  unsupported_ticker_transactions: UnsupportedTickerTransaction[];
  unresolved_transactions: UnresolvedTransaction[];
  new_transactions: Record<string, unknown>[];
  duplicate_transactions: Record<string, unknown>[];
}

export interface UploadTransactionsResponse {
  message: string | null;
  total_detected: number;
  success_count: number;
  error_count: number;
  errors: string[];
  user_error?: boolean;
  invalid_transactions: ImportIssueTransaction[];
  unsupported_currency_transactions: ImportIssueTransaction[];
  unsupported_ticker_transactions: UnsupportedTickerTransaction[];
  unresolved_transactions: UnresolvedTransaction[];
  new_transactions: Record<string, unknown>[];
  duplicate_transactions: Record<string, unknown>[];
  file_results: UploadTransactionsFileResult[];
}

export interface SyncProgressResponse {
  step: string | null;
  result: UploadTransactionsResponse | null;
}

export interface TransactionSelection {
  row_number: number;
  selected_match: PotentialMatch;
  transaction_data: Record<string, unknown>;
}

export interface ClarificationRequest {
  portfolio_id: number;
  selections: TransactionSelection[];
}

export interface ClarificationResponse {
  message: string;
  processed_count: number;
  errors: string[];
}

export type ApiTaxTier = {
  min_years: number;
  max_years?: number | null;
  rate: number;
};

export type ApiTaxRules = {
  version: number;
  type: "fixed" | "tiered";
  capital_gains: {
    holding_period_unit: "year";
    rate?: number;
    tiers?: ApiTaxTier[];
  };
  dividends: {
    rate: number;
  };
};

export interface TickerNotification {
  id: number;
  ticker: string | null;
  company_name: string | null;
  exchange: string | null;
  isin: string | null;
  currency: string;
  completed_at: string | null;
}

// TECH-DEBT: split candidate — UserSettings, UserSettingsUpdate, Country,
// ExchangeRateResponse, HistoricalExchangeRate could be co-located with settings.ts
export interface UserSettings {
  user_id: number;
  email?: string;
  display_name?: string | null;
  country?: string | null;
  preferred_display_currency: string;
  theme: string;
  is_tax_calculator_supported?: boolean;
  tax_rules?: ApiTaxRules | null;
  email_weekly_earnings?: boolean;
  email_transcript_ready?: boolean;
  push_weekly_earnings?: boolean;
  push_transcript_ready?: boolean;
  push_referral_rewards?: boolean;
  email_product_updates?: boolean;
  /** Whether the person uses the Oitava app on a phone, and allowed its notifications. */
  phone_app?: "none" | "notifications_off" | "notifications_on";
  onboarding_welcome_seen?: boolean;
  onboarding_analysis_seen?: boolean;
  language?: Language;
  is_google_linked?: boolean;
  is_apple_linked?: boolean;
  has_password?: boolean;
  holdings_column_visibility?: Record<string, boolean> | null;
  holdings_currency_display_mode?: string;
  trade_analysis_column_visibility?: Record<string, boolean> | null;
  watchlist_column_visibility?: Record<string, boolean> | null;
}

export interface UserSettingsUpdate {
  display_name?: string | null;
  country?: string | null;
  language?: Language;
  preferred_display_currency?: string;
  theme?: string;
  tax_rules?: ApiTaxRules | null;
  email_weekly_earnings?: boolean;
  email_transcript_ready?: boolean;
  push_weekly_earnings?: boolean;
  push_transcript_ready?: boolean;
  push_referral_rewards?: boolean;
  email_product_updates?: boolean;
  onboarding_welcome_seen?: boolean;
  onboarding_analysis_seen?: boolean;
  holdings_column_visibility?: Record<string, boolean> | null;
  holdings_currency_display_mode?: string;
  trade_analysis_column_visibility?: Record<string, boolean> | null;
  watchlist_column_visibility?: Record<string, boolean> | null;
}

// TECH-DEBT: split candidate — AppNotification could be co-located with notifications.ts
export interface AppNotification {
  id: number;
  type: string;
  title: string;
  body?: string | null;
  params: Record<string, string | number>;
  link?: string | null;
  is_read: boolean;
  created_at: string;
}

export interface ExchangeRateResponse {
  rate: number;
}

export interface Country {
  code: string;
  name: string;
  default_currency: Fiat;
  default_tax_rules?: ApiTaxRules | null;
}

// TECH-DEBT: split candidate — TWRResponse, AnnualTWRResponse, IRRResponse, BenchmarkKey,
// BenchmarkInfo, ShadowSeriesResponse, BenchmarkIRRResponse, TwrSeriesResponse,
// BenchmarkTWRResponse, BenchmarkAnnualTWRResponse, BenchmarkTWRItem, AnnualTWRPoint,
// TwrSeriesPoint, TradeAnalysisAsset, TradeAnalysisResponse could all be co-located with performance.ts
export interface TWRResponse {
  twr_value: number | null;
  timeframe: string;
  has_deposits: boolean;
  warning: string | null;
}

export interface AnnualTWRPoint {
  year: number;
  twr: number;
  is_ytd?: boolean;
}

export interface AnnualTWRResponse {
  annual_twr: AnnualTWRPoint[] | null;
  timeframe: string;
  has_deposits: boolean;
  warning: string | null;
  from_date?: string | null;
  to_date?: string | null;
  cagr?: number | null;
}

export type BenchmarkKey = "SP500" | "NASDAQ100" | "MSCI_WORLD";

export interface BenchmarkInfo {
  code: BenchmarkKey;
  display_name: string;
  proxy_symbol: string;
  currency: string;
}

export interface ShadowSeriesResponse {
  from_date: string;
  to_date: string;
  series: Record<string, HistoricalValuePoint[]>;
}

export interface BenchmarkTWRItem {
  key: BenchmarkKey;
  twr_value: number | null;
}

export interface BenchmarkTWRResponse {
  timeframe: string;
  has_deposits: boolean;
  warning: string | null;
  benchmarks: BenchmarkTWRItem[];
}

export interface BenchmarkAnnualTWRResponse {
  from_date: string;
  to_date: string;
  annual: Record<string, AnnualTWRPoint[]>;
  cagr?: Record<string, number | null>;
}

export interface BenchmarkIRRResponse {
  from_date: string;
  to_date: string;
  irr_map: Record<string, number | null>;
}

export interface TwrSeriesPoint {
  date: string;
  portfolio: number;
  [key: string]: number | string;
}

export interface TwrSeriesResponse {
  start_date: string | null;
  end_date: string | null;
  series: TwrSeriesPoint[];
}

export interface IRRResponse {
  irr_value: number | null;
  total_return: number | null;
  timeframe: string;
  has_deposits: boolean;
  warning: string | null;
}

export interface TradeAnalysisLot {
  quantity: number;
  buy_date: string;
  sell_date: string | null;
  days_held: number;
  cost_basis: number;
  current_value: number;
  realized_profit: number;
  unrealized_profit: number;
  dividend_income: number;
  total_profit: number;
  roi: number;
  fx_impact: number;
}

export interface TradeAnalysisAsset {
  id?: string;
  symbol: string;
  name: string;
  exchange: string;
  asset_type: "ETF" | "STOCK" | "CUSTOM";
  status: "HOLD" | "SOLD";
  quantity: number;
  invested: number;
  current_value: number;
  realized_profit: number;
  unrealized_profit: number;
  dividend_income: number;
  total_profit: number;
  roi: number;
  /** Null for the free tier, where FX impact and CAGR are withheld. */
  fx_impact: number | null;
  holding_period_days: number;
  holding_period_days_min: number;
  holding_period_days_max: number;
  cagr?: number | null;
  lots: TradeAnalysisLot[];
  /** Free tier only: how many lots were withheld, so the table can show a breakdown exists. */
  lot_count?: number | null;
}

export interface TradeAnalysisResponse {
  win_rate: number;
  profit_split_realized: number;
  profit_split_unrealized: number;
  avg_winner_hold_days: number;
  avg_loser_hold_days: number;
  overall_avg_hold_days: number;
  assets: TradeAnalysisAsset[];
}

// TECH-DEBT: split candidate — BillingStatus could be co-located with billing.ts
export interface BillingStatus {
  subscription_status:
    | "trialing"
    | "active"
    | "past_due"
    | "canceled"
    | "unpaid"
    | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  trial_ends_at: string;
  is_founding_member: boolean;
  billing_info_acknowledged: boolean;
  is_ff: boolean;
  /** Referred-friend free months earned but not yet applied to a bill. */
  pending_free_months: number;
  /** Master switch for the freemium behaviour. When false, an expired trial is still a lockout. */
  free_tier_enabled: boolean;
  /** Derived server-side, never stored. "trial" carries the same entitlements as "paid". */
  tier: "paid" | "trial" | "free";
  /** Set once the user answered the post-trial screen by staying free. NULL = not asked yet. */
  free_tier_choice_at: string | null;
  /** Free and over the portfolio allowance without having picked which ones stay editable. */
  needs_portfolio_pick: boolean;
  entitlements: Entitlements;
}

/** Resolved server-side from the tier. The frontend reads these and never recomputes them. */
export interface Entitlements {
  has_paid_access: boolean;
  max_portfolios: number;
  max_watchlists: number;
  max_watchlist_items: number;
  /** Cap on annual periods for the free tier; null means full quarterly/TTM history. */
  research_annual_years: number | null;
}

export type SubscriptionDetails =
  | { has_subscription: false }
  | {
      has_subscription: true;
      status: string;
      amount: number;
      currency: string;
      interval: "month" | "year";
      current_period_end: string;
      cancel_at_period_end: boolean;
    };

export type UploadProgressMap = Record<
  string,
  { total: number; processed: number }
>;

// TECH-DEBT: split candidate — Watchlist, WatchlistItem could be co-located with watchlists.ts
export interface Watchlist {
  id: number;
  user_id: number;
  name: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  items?: WatchlistItem[];
  item_listing_ids?: number[];
  /** Free tier over the watchlist allowance: viewable, stocks can be removed but not added. */
  is_locked?: boolean;
}

/** One table row of market data. Shared by watchlist items and the read-only market lists. */
export interface ListingMarketData {
  listing_id: number;
  listing: Listing;
  latest_price?: number | null;
  latest_price_date?: string | null;
  previous_price?: number | null;
  pe_ttm?: number | null;
  pe_fy1?: number | null;
  pe_fy2?: number | null;
  pe_fwd?: number | null;
  fy1_year?: number | null;
  fy2_year?: number | null;
  peg_fy1?: number | null;
  peg_fy2?: number | null;
  peg_ratio?: number | null;
  market_cap?: number | null;
  low_52w?: number | null;
  high_52w?: number | null;
  ma_50?: number | null;
  ma_200?: number | null;
  ytd_start_price?: number | null;
  year_ago_price?: number | null;
  price_7d_ago?: number | null;
  price_1m_ago?: number | null;
  price_3m_ago?: number | null;
  price_6m_ago?: number | null;
  next_earnings_date?: string | null;
  total_cash?: number | null;
  total_debt?: number | null;
  net_cash?: number | null;
  debt_to_equity?: number | null;
  ps_ratio?: number | null;
  net_margin?: number | null;
  dividend_yield_ttm?: number | null;
  /** REIT-only trailing Price/FFO; null for every other listing. */
  p_ffo?: number | null;
}

export interface WatchlistItem extends ListingMarketData {
  id: number;
  watchlist_id: number;
  notes?: string | null;
  added_at: string;
}

/** A read-only preset list (an index, a sector, a screen) offered beside the user's watchlists. */
export interface MarketListSummary {
  key: string;
  name: string;
}

export interface MarketList extends MarketListSummary {
  items: ListingMarketData[];
}

// TECH-DEBT: split candidate — SalesEvent, DividendEvent, TaxCalculationResult could be
// co-located with taxes.ts
export interface SalesEvent {
  ticker: string;
  date: string;
  purchase_date?: string;
  proceeds_local: number;
  cost_basis_local: number;
  net_gain_local: number;
  tax_amount_local: number;
  applied_rate: number;
  warnings?: string[];
  sell_transaction_id?: number;
}

export interface DividendEvent {
  ticker: string;
  date: string;
  gross_dividend_local: number;
  withheld_tax_local: number;
  tax_to_pay_local: number;
  applied_rate: number;
  gross_dividend: number;
  withheld_tax: number;
  country_code?: string | null;
  country_name?: string | null;
}

export interface InterestTaxEvent {
  year_month: string;
  gross_interest_local: number;
  withheld_tax_local: number;
  tax_to_pay_local: number;
  applied_rate: number;
}

export interface TaxCalculationResult {
  year: number;
  user_currency: string;
  sales: {
    total_capital_gains_tax: number;
    total_net_gain: number;
    events: SalesEvent[];
  };
  dividends: {
    total_gross_dividend: number;
    total_withheld_tax: number;
    total_tax_to_pay: number;
    withheld_tax_unavailable: boolean;
    events: DividendEvent[];
  };
  interest: {
    total_gross_interest: number;
    total_withheld_tax: number;
    total_tax_to_pay: number;
    events: InterestTaxEvent[];
  };
  summary: {
    total_estimated_tax_liability: number;
  };
}

// TECH-DEBT: split candidate — RealizedGain, DailyInterestTransaction,
// MonthlyInterestAggregation, InterestSummaryResponse could be co-located with gains.ts
export interface RealizedGain {
  id: number;
  portfolio_id: number;
  listing_id: number;
  listing?: Listing;
  sell_date: string;
  quantity_sold: number;
  native_currency: string;
  cost_basis_of_sold_shares_native: number;
  cost_basis_of_sold_shares_base: number;
  proceeds_from_sale_native: number;
  proceeds_from_sale_base: number;
  realized_gain_loss_native: number;
  realized_gain_loss_base: number;
  fx_impact_base_currency: number | null;
}

export type EditedTransactionPayload = Record<string, unknown>;

export interface SaveEditedTransactionsResponse {
  saved_count: number;
  unsupported_count?: number;
  unsupported_tickers?: string[];
  ambiguous_transactions?: { reason?: string }[];
}

export interface DailyInterestTransaction {
  id: number;
  date: string;
  amount_native: number;
  amount_base: number;
  withheld_tax_native: number;
  withheld_tax_base: number;
  currency_native: string;
  notes?: string;
  is_in_kind?: boolean;
  ticker?: string | null;
  quantity?: number | null;
}

export interface MonthlyInterestAggregation {
  year_month: string;
  total_interest_base: number;
  total_withheld_base: number;
  transaction_count: number;
  daily_transactions: DailyInterestTransaction[];
}

export interface InterestSummaryResponse {
  monthly_aggregations: MonthlyInterestAggregation[];
}

// TECH-DEBT: split candidate — TickerRequestCreate, TickerRequestResponse could be
// co-located with listings.ts
export interface TickerRequestCreate {
  ticker?: string | null;
  currency: string;
  isin?: string | null;
  exchange?: string | null;
  company_name?: string | null;
  brokerage?: string | null;
}

export interface TickerRequestResponse {
  id: number;
  user_id: number;
  ticker: string | null;
  currency: string;
  isin: string | null;
  exchange: string | null;
  company_name: string | null;
  status: string;
  created_at: string;
  completed_at: string | null;
}

export interface TickerRequestBatchItemResult {
  ticker: string | null;
  isin: string | null;
  currency: string;
  status: "created" | "duplicate" | "already_listed";
  message: string | null;
  request: TickerRequestResponse | null;
}

export interface TickerRequestBatchResponse {
  results: TickerRequestBatchItemResult[];
}

// TECH-DEBT: split candidate — SectorConcentrationItem, SectorConcentrationResponse could
// be co-located with holdings.ts
export interface SectorConcentrationItem {
  sector: string;
  market_value_base: number;
  weight: number;
  tickers: string[];
}

export interface SectorConcentrationResponse {
  total_market_value_base: number;
  items: SectorConcentrationItem[];
}

export interface CountryConcentrationItem {
  country: string;
  market_value_base: number;
  weight: number;
  tickers: string[];
}

export interface CountryConcentrationResponse {
  total_market_value_base: number;
  items: CountryConcentrationItem[];
}

/**
 * Reference closes for one held listing. Every price is the raw close in the listing's own
 * quote currency, with no FX conversion. The matching current price on BackendHolding is
 * `current_price_quote` when set, otherwise `current_price` (they only differ when a broker
 * reports in the account currency). Never pair these with `current_price_base`.
 * Null when the listing has no price for that period (custom assets, new listings).
 */
export interface HoldingPriceReference {
  listing_id: number;
  ticker: string;
  exchange: string;
  previous_price: number | null;
  price_7d_ago: number | null;
  price_1m_ago: number | null;
  price_3m_ago: number | null;
  price_6m_ago: number | null;
  ytd_start_price: number | null;
  year_ago_price: number | null;
  price_2y_ago: number | null;
  price_5y_ago: number | null;
}

export interface HoldingsValuationMetricsResponse {
  pe_ttm: number | null;
  pe_ttm_coverage_pct: number;
  revenue_growth_ttm: number | null;
  revenue_growth_ttm_coverage_pct: number;
  eps_growth_ttm: number | null;
  eps_growth_ttm_coverage_pct: number;
}
